<template>
  <div v-if="module" class="module-detail-container">
    <!-- Compact Hero -->
    <div class="module-hero px-4 px-md-6 pt-4 pt-md-6 pb-5 pb-md-8">
      <div class="module-hero-wrap">
      <div class="hero-glow"></div>
      <div class="hero-inner" style="position: relative; z-index: 1">
        <div class="d-flex align-center mb-3 flex-wrap gap-2">
          <v-btn icon dark @click="$router.push('/akademie/suite')" class="mr-1">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-chip class="gold-border" :color="tierColor" small outlined>
            <v-icon left small :color="tierColor">mdi-medal-outline</v-icon>
            {{ tierLabel }} Stufe
          </v-chip>
        </div>

        <div class="d-flex flex-wrap align-end justify-space-between">
          <div class="hero-text" style="min-width: 0; flex: 1 1 auto">
            <h1 class="hero-title font-weight-bold mb-1">{{ module.title }}</h1>
            <p class="hero-subtitle grey--text text--lighten-1 mb-3">{{ module.description }}</p>

            <!-- Info-Strip (jetzt waagerecht, mit Trennlinien, ohne Wrap-Chaos) -->
            <div class="info-strip d-flex flex-wrap">
              <div class="info-stat">
                <v-icon color="#D4AF37" size="20" class="mr-2">mdi-play-circle-outline</v-icon>
                <div>
                  <div class="info-value">{{ (module.lessons && module.lessons.length) || 0 }}</div>
                  <div class="info-label">Lektionen</div>
                </div>
              </div>
              <div class="info-stat">
                <v-icon color="#D4AF37" size="20" class="mr-2">mdi-clock-outline</v-icon>
                <div>
                  <div class="info-value">{{ module.estimatedMinutes || 0 }}<small> Min</small></div>
                  <div class="info-label">Dauer</div>
                </div>
              </div>
              <div class="info-stat">
                <v-icon color="#D4AF37" size="20" class="mr-2">mdi-toolbox-outline</v-icon>
                <div>
                  <div class="info-value">{{ (module.toolbox && module.toolbox.length) || 0 }}</div>
                  <div class="info-label">Materialien</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Progress Ring -->
          <div class="progress-block mt-4 mt-md-0 ml-md-6">
            <v-progress-circular :value="getOverallProgress()" color="#D4AF37" size="84" width="6">
              <div class="text-center">
                <div class="progress-ring-value gold-text">{{ getOverallProgress() }}<small>%</small></div>
                <div class="progress-ring-label">fertig</div>
              </div>
            </v-progress-circular>
          </div>
        </div>
      </div>
      </div>
    </div>

    <!-- Content -->
    <v-container fluid class="content-section pa-4 pa-md-6" style="max-width: 1100px">
      <!-- "Was lerne ich?"-Block -->
      <div v-if="module.longDescription" class="learn-block rounded-xl pa-4 pa-md-6 mb-8">
        <div class="learn-glow"></div>
        <div class="d-flex align-center mb-3">
          <div class="learn-icon-wrap mr-3">
            <v-icon color="#D4AF37">mdi-lightbulb-on-outline</v-icon>
          </div>
          <div>
            <div class="text-caption gold-text font-weight-bold" style="letter-spacing: 2px; text-transform: uppercase">Was lerne ich?</div>
            <div class="text-subtitle-2 grey--text">Lernziele &amp; Inhalt</div>
          </div>
        </div>
        <div class="learn-text" v-html="formattedLongDescription"></div>
      </div>

      <h2 class="section-title text-h5 font-weight-bold white--text mb-5">
        <v-icon color="#D4AF37" class="mr-2">mdi-format-list-numbered</v-icon> Lektionen
      </h2>

      <v-timeline dense align-top dark>
        <v-timeline-item
          v-for="(lesson, index) in module.lessons"
          :key="lesson._id"
          :color="isLessonCompleted(lesson._id) ? '#D4AF37' : 'grey darken-3'"
          :icon="isLessonCompleted(lesson._id) ? 'mdi-check' : isLessonUnlocked(index) ? 'mdi-play' : 'mdi-lock'"
          fill-dot>
          <v-card
            class="lesson-card rounded-lg pa-3 pa-md-4"
            :class="{ locked: !isLessonUnlocked(index), completed: isLessonCompleted(lesson._id) }"
            @click="openLesson(lesson, index)">
            <div class="d-flex align-center">
              <div style="min-width: 0; flex: 1">
                <h3 class="lesson-title text-subtitle-1 text-md-h6 white--text mb-1 text-truncate">{{ index + 1 }}. {{ lesson.title }}</h3>
                <div class="d-flex align-center grey--text text-caption flex-wrap">
                  <v-icon small color="grey" class="mr-1">{{ getIconForType(lesson.contentType) }}</v-icon>
                  <span class="text-capitalize">{{ lesson.contentType }}</span>
                  <span class="mx-2">·</span>
                  <span>{{ lesson.estimatedMinutes || 0 }} Min</span>
                </div>
              </div>
              <v-btn v-if="isLessonUnlocked(index)" icon color="#D4AF37">
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </v-card>
        </v-timeline-item>

        <!-- Quiz Item -->
        <v-timeline-item
          v-if="module.quiz"
          :color="isQuizCompleted() ? '#D4AF37' : 'grey darken-3'"
          :icon="isQuizCompleted() ? 'mdi-trophy' : 'mdi-help-circle-outline'"
          fill-dot>
          <v-card
            class="lesson-card quiz-card rounded-lg pa-3 pa-md-4"
            :class="{ locked: !isQuizUnlocked(), completed: isQuizCompleted() }"
            @click="openQuiz()">
            <div class="d-flex justify-space-between align-center">
              <div style="min-width: 0">
                <h3 class="text-h6 gold-text mb-1">Abschlusstest</h3>
                <div class="grey--text text-caption">Beweise dein Wissen, um das Modul abzuschliessen</div>
              </div>
              <v-icon color="#D4AF37" size="32" v-if="isQuizCompleted()">mdi-medal</v-icon>
              <v-btn v-else-if="isQuizUnlocked()" color="#D4AF37" outlined rounded small>Starten</v-btn>
            </div>
          </v-card>
        </v-timeline-item>
      </v-timeline>

      <!-- TOOLBOX -->
      <div v-if="module.toolbox && module.toolbox.length" class="toolbox-section mt-8">
        <h2 class="section-title text-h5 font-weight-bold white--text mb-1">
          <v-icon color="#D4AF37" class="mr-2">mdi-toolbox-outline</v-icon> Toolbox &amp; Materialien
        </h2>
        <p class="text-caption grey--text mb-4">Zusatzmaterialien zu diesem Modul</p>

        <div class="toolbox-grid">
          <v-card v-for="item in module.toolbox" :key="item._id" class="toolbox-item rounded-xl pa-3 pa-md-4 d-flex flex-column" dark elevation="0">
            <div class="d-flex align-start mb-3">
              <div class="toolbox-icon mr-3" :style="{ background: categoryBg(item.category) }">
                <v-icon size="22" :color="categoryColor(item.category)">{{ categoryIcon(item.category) }}</v-icon>
              </div>
              <div class="flex-grow-1" style="min-width: 0">
                <div class="item-title font-weight-bold text-truncate" :title="item.title || item.originalName">
                  {{ item.title || item.originalName }}
                </div>
                <div class="text-caption grey--text d-flex align-center flex-wrap">
                  <span class="text-uppercase">{{ item.category }}</span>
                  <template v-if="item.size">
                    <span class="mx-2">·</span>
                    <span>{{ formatSize(item.size) }}</span>
                  </template>
                </div>
              </div>
            </div>

            <!-- Mini Preview -->
            <div v-if="item.category === 'image'" class="item-preview mb-3" @click="openToolbox(item)">
              <SecuredMedia :src="toolboxPath(item)" media-type="image" />
              <div class="preview-hover-icon">
                <v-icon size="32" color="white">mdi-magnify-plus-outline</v-icon>
              </div>
            </div>
            <div v-else-if="item.category === 'text'" class="text-caption grey--text mt-1 mb-3 text-clip-3 text-note">
              {{ item.textContent }}
            </div>

            <v-spacer />

            <div class="item-actions d-flex align-center">
              <v-btn v-if="item.category !== 'text'" small text color="#D4AF37" class="text-none mr-1" @click="openToolbox(item)">
                <v-icon left small>mdi-eye-outline</v-icon> Ansehen
              </v-btn>
              <v-btn v-if="item.category !== 'text'" small text color="grey lighten-1" class="text-none" :loading="downloadingId === item._id" @click="downloadItem(item)">
                <v-icon left small>mdi-download-outline</v-icon> Download
              </v-btn>
            </div>
          </v-card>
        </div>
      </div>
    </v-container>

    <!-- Preview Dialog -->
    <v-dialog v-model="previewDialog.show" max-width="900" dark>
      <v-card v-if="previewDialog.item" style="background-color: #1e1e1e">
        <v-card-title class="d-flex align-center">
          <v-icon left :color="categoryColor(previewDialog.item.category)">{{ categoryIcon(previewDialog.item.category) }}</v-icon>
          <span class="text-truncate">{{ previewDialog.item.title || previewDialog.item.originalName }}</span>
          <v-spacer />
          <v-btn icon @click="downloadItem(previewDialog.item)" :loading="downloadingId === previewDialog.item._id">
            <v-icon>mdi-download</v-icon>
          </v-btn>
          <v-btn icon @click="previewDialog.show = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <SecuredMedia
            :src="toolboxPath(previewDialog.item)"
            :media-type="previewDialog.item.category"
            :download-name="previewDialog.item.originalName"
            eager />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import SecuredMedia from "./SecuredMedia.vue"

export default {
  name: "ModuleDetail",
  components: { SecuredMedia },
  data() {
    return {
      module: null,
      progress: null,
      loading: false,
      previewDialog: { show: false, item: null },
      downloadingId: null,
    }
  },
  computed: {
    tierLabel() {
      if (!this.module || !this.module.tier) return ""
      return this.module.tier.charAt(0).toUpperCase() + this.module.tier.slice(1)
    },
    tierColor() {
      const map = { bronze: "#CD7F32", silber: "#C0C0C0", gold: "#FFD700" }
      return (this.module && map[this.module.tier]) || "#D4AF37"
    },
    formattedLongDescription() {
      if (!this.module || !this.module.longDescription) return ""
      // Newlines zu <br>, sehr simpel; XSS-Schutz: keine HTML-Tags durchlassen
      const safe = this.module.longDescription.replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]))
      return safe.replace(/\n/g, "<br />")
    },
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const [modRes, progRes] = await Promise.all([api.akademie.fetchModule(this.$route.params.id), api.akademie.fetchProgress()])
        this.module = modRes
        if (progRes && Array.isArray(progRes)) {
          this.progress = progRes.find((p) => p.module === this.$route.params.id || (p.module && p.module._id === this.$route.params.id)) || null
        }
      } catch (err) {
        console.error("Fehler beim Laden des Moduls:", err)
      } finally {
        this.loading = false
      }
    },
    getIconForType(type) {
      const map = {
        video: "mdi-video-outline",
        audio: "mdi-music-note-outline",
        text: "mdi-text-box-outline",
        image: "mdi-image-outline",
        pdf: "mdi-file-pdf-box",
        file: "mdi-file-outline",
      }
      return map[type] || "mdi-file"
    },
    isLessonCompleted(lessonId) {
      if (!this.progress || !this.progress.lessons) return false
      const lp = this.progress.lessons.find((l) => l.lesson === lessonId || (l.lesson && l.lesson._id === lessonId))
      return lp && lp.completedAt != null
    },
    isLessonUnlocked(index) {
      if (index === 0) return true
      const prevLessonId = this.module.lessons[index - 1]._id
      return this.isLessonCompleted(prevLessonId)
    },
    isQuizUnlocked() {
      if (!this.module || !this.module.lessons) return false
      return this.module.lessons.every((l) => this.isLessonCompleted(l._id))
    },
    isQuizCompleted() {
      return this.progress && this.progress.completedAt != null
    },
    getOverallProgress() {
      if (!this.progress || !this.module || !this.module.lessons) return 0
      if (this.progress.completedAt) return 100
      const completed = this.module.lessons.filter((l) => this.isLessonCompleted(l._id)).length
      const total = this.module.lessons.length + 1
      return Math.floor((completed / total) * 100)
    },
    openLesson(lesson, index) {
      if (this.isLessonUnlocked(index)) {
        this.$router.push(`/akademie/modules/${this.module._id}/lessons/${lesson._id}`)
      }
    },
    openQuiz() {
      if (this.isQuizUnlocked()) {
        this.$router.push(`/akademie/modules/${this.module._id}/quiz`)
      }
    },
    openToolbox(item) {
      this.previewDialog = { show: true, item }
    },
    toolboxPath(item) {
      return api.akademie.getToolboxItemPath(this.module._id, item._id)
    },
    async downloadItem(item) {
      this.downloadingId = item._id
      try {
        await api.akademie.downloadToolboxItem(this.module._id, item._id, item.originalName || item.title || "datei")
      } catch (e) {
        console.error("download error", e)
      } finally {
        this.downloadingId = null
      }
    },
    categoryIcon(cat) {
      const map = {
        image: "mdi-image-outline",
        video: "mdi-video-outline",
        audio: "mdi-music-note-outline",
        pdf: "mdi-file-pdf-box",
        text: "mdi-text-box-outline",
        file: "mdi-file-outline",
      }
      return map[cat] || "mdi-file-outline"
    },
    categoryColor(cat) {
      const map = {
        image: "#42a5f5",
        video: "#ef5350",
        audio: "#ab47bc",
        pdf: "#e53935",
        text: "#66bb6a",
        file: "#bdbdbd",
      }
      return map[cat] || "#bdbdbd"
    },
    categoryBg(cat) {
      const c = this.categoryColor(cat)
      // RGBA mit Transparenz
      const map = {
        "#42a5f5": "rgba(66, 165, 245, 0.12)",
        "#ef5350": "rgba(239, 83, 80, 0.12)",
        "#ab47bc": "rgba(171, 71, 188, 0.12)",
        "#e53935": "rgba(229, 57, 53, 0.12)",
        "#66bb6a": "rgba(102, 187, 106, 0.12)",
        "#bdbdbd": "rgba(189, 189, 189, 0.1)",
      }
      return map[c] || "rgba(255,255,255,0.04)"
    },
    formatSize(b) {
      if (!b) return ""
      if (b < 1024) return b + " B"
      if (b < 1024 * 1024) return (b / 1024).toFixed(1) + " KB"
      return (b / 1024 / 1024).toFixed(1) + " MB"
    },
  },
}
</script>

<style scoped>
.module-detail-container {
  min-height: 100vh;
  background-color: #0d0d0d;
}

.gold-text {
  background: linear-gradient(45deg, #d4af37, #ffd700);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.gold-border { border-color: #d4af37 !important; }
.gap-2 { gap: 8px; }

/* ===== HERO (kompakt, kein riesiger Top-Abstand) ===== */
.module-hero {
  position: relative;
  background: linear-gradient(135deg, rgba(35, 30, 20, 0.85) 0%, rgba(15, 15, 15, 0.98) 70%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  overflow: hidden;
}
.module-hero-wrap {
  max-width: 1100px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}
.hero-glow {
  position: absolute;
  width: 320px;
  height: 320px;
  border-radius: 50%;
  filter: blur(90px);
  background: rgba(212, 175, 55, 0.14);
  top: -120px;
  right: -80px;
  z-index: 0;
}

.hero-title {
  color: #fff;
  line-height: 1.2;
  font-size: 1.6rem;
  word-break: break-word;
}
.hero-subtitle {
  font-size: 0.9rem;
  max-width: 640px;
}
@media (min-width: 600px) { .hero-title { font-size: 2rem; } }
@media (min-width: 960px) { .hero-title { font-size: 2.4rem; } }

/* Info-Strip: horizontal, eigene Pillen, kein Vertikal-Stack mehr */
.info-strip {
  gap: 8px;
  margin-top: 12px;
}
.info-stat {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 999px;
  padding: 8px 14px;
  min-width: 0;
}
.info-value {
  font-size: 0.95rem;
  font-weight: 700;
  color: white;
  line-height: 1;
}
.info-value small {
  font-weight: 500;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.6);
  margin-left: 2px;
}
.info-label {
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

.progress-block {
  flex-shrink: 0;
}
.progress-ring-value {
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1;
}
.progress-ring-value small {
  font-size: 0.7rem;
  opacity: 0.7;
}
.progress-ring-label {
  font-size: 0.6rem;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  margin-top: 2px;
  letter-spacing: 0.5px;
}

/* ===== CONTENT ===== */
.content-section {
  margin: 0 auto;
}

.section-title {
  display: flex;
  align-items: center;
}

.learn-block {
  position: relative;
  background: linear-gradient(135deg, rgba(212, 175, 55, 0.06) 0%, rgba(30, 30, 30, 0.85) 100%);
  border: 1px solid rgba(212, 175, 55, 0.2);
  overflow: hidden;
  animation: fadeUp 0.5s ease both;
}
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
.learn-glow {
  position: absolute;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  filter: blur(80px);
  background: rgba(212, 175, 55, 0.12);
  top: -140px;
  right: -60px;
  pointer-events: none;
}
.learn-icon-wrap {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(212, 175, 55, 0.12);
  border: 1px solid rgba(212, 175, 55, 0.2);
  flex-shrink: 0;
}
.learn-text {
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.7;
  font-size: 0.97rem;
  position: relative;
  z-index: 1;
}

.lesson-card {
  background: rgba(30, 30, 30, 0.85) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: all 0.25s ease;
}
.lesson-card:hover:not(.locked) {
  border-color: rgba(212, 175, 55, 0.45);
  transform: translateX(6px);
}
.lesson-card.locked {
  opacity: 0.5;
  cursor: not-allowed;
}
.lesson-card.completed {
  border-left: 4px solid #d4af37;
}
.lesson-title {
  font-size: 1rem;
}

.quiz-card {
  background: rgba(40, 35, 20, 0.85) !important;
  border: 1px dashed rgba(212, 175, 55, 0.35);
}

/* ===== TOOLBOX ===== */
.toolbox-section {
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 14px;
  padding: 18px;
}
.toolbox-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 14px;
}
.toolbox-item {
  background: rgba(28, 28, 28, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: border-color 0.2s, transform 0.2s;
}
.toolbox-item:hover {
  border-color: rgba(212, 175, 55, 0.35);
}

.toolbox-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.item-title {
  font-size: 0.95rem;
  line-height: 1.25;
}

.item-preview {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  background: rgba(0, 0, 0, 0.3);
}
.item-preview :deep(.secured-media-content) {
  max-height: 140px;
  width: 100%;
  object-fit: cover;
  display: block;
  border-radius: 0;
}
.preview-hover-icon {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
  opacity: 0;
  transition: opacity 0.2s ease;
}
.item-preview:hover .preview-hover-icon {
  opacity: 1;
}

.text-note {
  background: rgba(102, 187, 106, 0.06);
  border-left: 3px solid #66bb6a;
  padding: 8px 10px;
  border-radius: 6px;
}
.text-clip-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.item-actions {
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  padding-top: 6px;
  margin-top: 4px;
}

@media (max-width: 600px) {
  .info-stat { padding: 6px 10px; }
  .info-value { font-size: 0.85rem; }
  .progress-block { width: 100%; display: flex; justify-content: center; }
}
</style>
